import React, { useState, useEffect, useContext } from "react";
import {
  Button,
  Container,
  Chip,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Grid,
  TextField,
  Divider
} from "@mui/material";
import LocationFormDialog from "../sections/locations/LocationFormDialog";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { DataGrid } from "@mui/x-data-grid";
import { AppContext } from "../context/AppProvider";
import { AuthContext } from "../context/AuthProvider";
import { cloneDeep } from "lodash";
import ConfirmDialog from "../components/ConfirmDialog";
import { fetchWithInterceptor } from "../utils/fetchWithInterceptor";

const PAGE_SIZE = 20;

export default function LocationsPage() {
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);

  const appContext = useContext(AppContext);
  const authContext = useContext(AuthContext);

  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogRowId, setDialogRowId] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [countries, setCountries] = useState([]);
  const [regions, setRegions] = useState([]);


  const [filters, setFilters] = useState({
    name: "",
    type: "",
    parentCountryId: "",
    parentRegionId: "",
    isActive: "",
  });

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: PAGE_SIZE,
  });

  const defaultFilter = {
    name: "",
    type: "",
    parentCountryId: "",
    parentRegionId: "",
    isActive: "",
  };

  const handleReset = () => setFilters(cloneDeep(defaultFilter));

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleMenuOpen = (event, location) => {
    setAnchorEl(event.currentTarget);
    setSelectedLocation(location);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleToggleStatus = async () => {
    if (!selectedLocation) return;

    try {
      const newStatus = !selectedLocation.isActive;

      const res = await fetchWithInterceptor(
        `/api/locations/${selectedLocation.id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            "x-access-token": authContext.getToken(),
          },
          body: JSON.stringify({ isActive: newStatus }),
        }
      );

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.message || "Failed updating location status");
      }

      appContext.showMessage(
        newStatus ? "Location activated" : "Location deactivated",
        "success"
      );

      handleMenuClose();
      loadLocations(true);
    } catch (err) {
      console.error(err);
      appContext.showMessage(err.message || "Error updating location status", "error");
    }
  };

  const handleToggleStatusClick = () => {
    setConfirmOpen(true);
  };

  const handleEdit = (location) => {
    setSelectedLocation(location);
    setDialogRowId(location.id);
    setDialogOpen(true);
  };

  const handleSaveLocation = async (payload) => {
    try {
      const method = dialogRowId ? "PUT" : "POST";
      const url = dialogRowId
        ? `/api/locations/${dialogRowId}`
        : "/api/locations";

      const res = await fetchWithInterceptor(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          "x-access-token": authContext.getToken(),
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.message || "Failed saving location");
      }

      appContext.showMessage(
        dialogRowId ? "Location updated successfully" : "Location created successfully",
        "success"
      );

      setDialogOpen(false);
      loadLocations(true);
    } catch (err) {
      console.error(err);
      appContext.showMessage(
        err.message || "Error saving location",
        "error"
      );
    }
  };

  const loadLocations = async (reset = false) => {

    try {
      setLoading(true);

      const params = new URLSearchParams();
      if (filters.name) params.append("name", filters.name);
      if (filters.type) params.append("type", filters.type);
      if (filters.parentCountryId) params.append("parentCountryId", filters.parentCountryId);
      if (filters.parentRegionId) params.append("parentRegionId", filters.parentRegionId);
      if (filters.isActive !== "") params.append("isActive", filters.isActive);

      params.append("limit", PAGE_SIZE);
      params.append("offset", reset ? 0 : paginationModel.page * PAGE_SIZE);

      const url = `/api/locations?${params.toString()}`;

      const response = await fetchWithInterceptor(url, {
        headers: {
          "Content-Type": "application/json",
          "x-access-token": authContext.getToken(),
        },
      });

      if (!response.ok) throw new Error("Failed to fetch locations");

      const data = await response.json();

      setLocations(data.rows || []);
      setRowCount(data.total || 0);

      if (reset) setPaginationModel((prev) => ({ ...prev, page: 0 }));
    } catch (err) {
      console.error(err);
      appContext.showMessage("Error loading locations", "error");
    } finally {
      setLoading(false);
    }
  };

  const loadFilterOptions = async () => {
    try {
      const [countriesRes, regionsRes] = await Promise.all([
        fetchWithInterceptor("/api/locations?type=country&limit=500", {
          headers: {
            "Content-Type": "application/json",
            "x-access-token": authContext.getToken(),
          },
        }),
        fetchWithInterceptor("/api/locations?type=region&limit=500", {
          headers: {
            "Content-Type": "application/json",
            "x-access-token": authContext.getToken(),
          },
        }),
      ]);

      if (countriesRes.ok) {
        const countriesData = await countriesRes.json();
        const list = countriesData.rows ?? countriesData.data ?? [];
        setCountries(Array.isArray(list) ? list : []);
      }
      if (regionsRes.ok) {
        const regionsData = await regionsRes.json();
        const list = regionsData.rows ?? regionsData.data ?? [];
        setRegions(Array.isArray(list) ? list : []);
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    loadFilterOptions();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    loadLocations(false);
    // eslint-disable-next-line
  }, [paginationModel.page]);

  useEffect(() => {
    loadLocations(true);
    // eslint-disable-next-line
  }, [filters]);

  const columns = [
    {
      field: "type",
      headerName: "TYPE",
      flex: 1,
      renderCell: (params) => (
        <Chip label={params.row.type?.toUpperCase()} size="small" />
      ),
    },
    {
      field: "name",
      headerName: "NAME",
      flex: 1,
      minWidth: 180,
    },
    {
      field: "parentCountry",
      headerName: "COUNTRY",
      flex: 1,
      renderCell: (params) =>
        params.row.parentCountry?.name || "-",
    },
    {
      field: "parentRegion",
      headerName: "REGION",
      flex: 1,
      renderCell: (params) => params.row.parentRegion?.name || "-",
    },
    {
      field: "isActive",
      headerName: "STATUS",
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.row.isActive ? "Active" : "Inactive"}
          color={params.row.isActive ? "success" : "default"}
          size="small"
        />
      ),
    },
    {
      field: "actions",
      headerName: "ACTIONS",
      width: 150,
      renderCell: (params) => (
        <>
          <Button
            size="small"
            variant="text"
            sx={{ mr: 1, minWidth: "auto", p: 0.5 }}
            onClick={() => handleEdit(params.row)}
          >
            EDIT
          </Button>
          <IconButton size="small" onClick={(e) => handleMenuOpen(e, params.row)}>
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </>
      ),
    },
  ];

  return (
    <>
      <Typography variant="body1" sx={{ ml: 3, mt: 3, mr: 5, mb: 1, color: "text.secondary" }}>
        Create, edit, and deactivate our tree of countries, regions, and subregions. Changes made here will be consumed by other modules in the ZYN web app (e.g., product creation).
      </Typography>

      <Container sx={{ pb: 3 }} component="main" maxWidth="100%">
        <Paper
          sx={{ width: "100%", backgroundColor: "background.paper", mt: 2, mb: 2 }}
        >
          <div style={{ padding: 16 }}>
            <Typography variant="h6">Search Filters</Typography>
            <br />
            <Grid container spacing={2} alignItems="center" sx={{ px: 0 }}>
              <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                <TextField
                  label="Name"
                  name="name"
                  variant="outlined"
                  fullWidth
                  autoComplete="off"
                  value={filters.name}
                  onChange={handleFilterChange}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                <TextField
                  select
                  label="Type"
                  name="type"
                  variant="outlined"
                  fullWidth
                  value={filters.type}
                  onChange={handleFilterChange}
                  slotProps={{
                    select: {
                      autoComplete: "off",
                    },
                  }}
                >
                  <MenuItem value="">ALL TYPES</MenuItem>
                  <MenuItem value="country">Country</MenuItem>
                  <MenuItem value="region">Region</MenuItem>
                  <MenuItem value="subregion">Subregion</MenuItem>
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                <TextField
                  select
                  label="Country"
                  name="parentCountryId"
                  variant="outlined"
                  fullWidth
                  value={filters.parentCountryId === "" ? "" : String(filters.parentCountryId)}
                  onChange={handleFilterChange}
                  slotProps={{
                    select: {
                      autoComplete: "off",
                    },
                  }}
                >
                  <MenuItem value="">ALL COUNTRIES</MenuItem>
                  {countries.map((c) => (
                    <MenuItem key={c.id} value={String(c.id)}>
                      {c.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                <TextField
                  select
                  label="Region"
                  name="parentRegionId"
                  variant="outlined"
                  fullWidth
                  value={filters.parentRegionId === "" ? "" : String(filters.parentRegionId)}
                  onChange={handleFilterChange}
                  slotProps={{
                    select: {
                      autoComplete: "off",
                    },
                  }}
                >
                  <MenuItem value="">ALL REGIONS</MenuItem>
                  {regions.map((r) => (
                    <MenuItem key={r.id} value={String(r.id)}>
                      {r.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                <TextField
                  select
                  label="Status"
                  name="isActive"
                  variant="outlined"
                  fullWidth
                  value={filters.isActive}
                  onChange={handleFilterChange}
                >
                  <MenuItem value="">ALL</MenuItem>
                  <MenuItem value="true">Active</MenuItem>
                  <MenuItem value="false">Inactive</MenuItem>
                </TextField>
              </Grid>
              <Grid
                size={{ xs: 12, md: 2 }}
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                }}
              >
                <Button onClick={handleReset}>RESET FILTER</Button>
              </Grid>
            </Grid>
          </div>

          <Divider />

          <Grid container sx={{ px: 4, pb: 2, pt: 2, justifyContent: "flex-end" }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                setDialogRowId(null);
                setSelectedLocation(null);
                setDialogOpen(true);
              }}
            >
              CREATE LOCATION
            </Button>
          </Grid>

          <DataGrid
            rows={locations}
            columns={columns}
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            rowCount={rowCount}
            pageSizeOptions={[PAGE_SIZE]}
            disableSelectionOnClick
            disableColumnFilter
            loading={loading}
            getRowId={(row) => row.id}
            sx={{
              height: 52 * PAGE_SIZE + 120,
              border: "none",
              "& .MuiDataGrid-menuIcon": { display: "none" },
              "& .MuiDataGrid-columnHeaderTitle": {
                textTransform: "uppercase",
                fontWeight: 600,
                fontSize: "0.75rem",
                color: "rgba(58, 53, 65, 0.87)",
              },
              "& .MuiDataGrid-columnsContainer": {
                backgroundColor: "rgb(249, 250, 252)",
              },
            }}
          />
        </Paper>

        <Menu open={!!anchorEl} anchorEl={anchorEl} onClose={handleMenuClose}>
          <MenuItem onClick={handleToggleStatusClick}>
            {selectedLocation?.isActive ? "Deactivate" : "Activate"}
          </MenuItem>
        </Menu>

        <LocationFormDialog
          open={dialogOpen}
          rowId={dialogRowId}
          location={selectedLocation}
          onClose={() => {
            setDialogOpen(false);
            setDialogRowId(null);
            setSelectedLocation(null);
          }}
          onSave={handleSaveLocation}
        />

        <ConfirmDialog
          open={confirmOpen}
          title={
            selectedLocation?.isActive
              ? "Deactivate Location"
              : "Activate Location"
          }
          message={
            selectedLocation?.isActive ? (
              <>
                Are you sure you want to deactivate
                <strong style={{ fontWeight: 500 }}>
                  {" "}
                  "{selectedLocation?.name}"?
                </strong>
              </>
            ) : (
              <>
                Are you sure you want to activate
                <strong style={{ fontWeight: 500 }}>
                  {" "}
                  "{selectedLocation?.name}"?
                </strong>
              </>
            )
          }
          onCancel={() => setConfirmOpen(false)}
          onConfirm={async () => {
            setConfirmOpen(false);
            await handleToggleStatus();
          }}
        />
      </Container>
    </>
  );
}
